import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { timeAgo } from '@/lib/format';
import { ArrowLeft, ExternalLink } from 'lucide-react';

interface NewsPost {
  id: number;
  title: string;
  content: string;
  source_url: string | null;
  is_ai_news: boolean;
  auto_source: string;
  created_at: string;
  board_name: string;
}

export function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['news', id],
    queryFn: () => get<NewsPost>(`/posts/${id}`),
    enabled: !!id,
  });

  if (isLoading) {
    return <Skeleton className="m-5 h-96" />;
  }
  if (!data) return null;

  return (
    <div className="mx-auto max-w-3xl px-5 py-6">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-text-sub)]"
      >
        <ArrowLeft className="h-4 w-4" /> 목록으로
      </button>

      <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <div className="mb-2 flex items-center gap-2">
          <Badge variant="primary">{data.board_name}</Badge>
          {data.is_ai_news && <Badge variant="editor">🤖 AI 요약</Badge>}
        </div>
        <h1 className="mb-2 text-2xl font-extrabold">{data.title}</h1>
        <div className="mb-4 text-xs text-[var(--color-text-sub)]">{timeAgo(data.created_at)}</div>

        <div
          className="prose prose-sm max-w-none post-content"
          dangerouslySetInnerHTML={{ __html: data.content }}
        />

        <p className="mt-8 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-text-sub)]">
          본 기사는 AI가 작성한 콘텐츠입니다.
        </p>

        {data.source_url && (
          <Button
            variant="secondary"
            className="mt-6"
            onClick={() => window.open(data.source_url!, '_blank')}
          >
            <ExternalLink className="h-4 w-4" />
            원문 보기
          </Button>
        )}
      </article>
    </div>
  );
}
